/**
 * OnboardingWizard — Multi-step wizard container.
 *
 * Steps: 1. Create Org → 2. Connect Channel → 3. Configure Escalation → 4. Complete
 * Manages current step state, navigates to /dashboard on completion.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/Card";
import { StepCreateOrg } from "@/components/onboarding/StepCreateOrg";
import { StepConnectChannel } from "@/components/onboarding/StepConnectChannel";
import { StepConfigureEscalation } from "@/components/onboarding/StepConfigureEscalation";
import { StepComplete } from "@/components/onboarding/StepComplete";

const steps = [
  { number: 1, label: "Create Org" },
  { number: 2, label: "Connect Channel" },
  { number: 3, label: "Configure Escalation" },
  { number: 4, label: "Complete" },
];

export function OnboardingWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [orgId, setOrgId] = useState<string | null>(null);

  function handleOrgCreated(id: string) {
    setOrgId(id);
    setCurrentStep(2);
  }

  function handleChannelDone() {
    setCurrentStep(3);
  }

  function handleEscalationDone() {
    setCurrentStep(4);
  }

  function handleFinish() {
    navigate("/dashboard");
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface px-4 py-12">
      {/* Logo */}
      <h1 className="mb-8 text-2xl font-bold tracking-tight text-primary">
        AJNT
      </h1>

      {/* Step indicator */}
      <div className="mb-8 flex items-center gap-2" role="navigation" aria-label="Wizard steps">
        {steps.map((step, i) => (
          <div key={step.number} className="flex items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                step.number < currentStep
                  ? "bg-success text-white"
                  : step.number === currentStep
                    ? "bg-primary text-white"
                    : "bg-surface-raised text-muted-foreground border border-white/10"
              }`}
              aria-current={step.number === currentStep ? "step" : undefined}
            >
              {step.number < currentStep ? "✓" : step.number}
            </div>
            <span
              className={`hidden text-xs sm:inline ${
                step.number === currentStep
                  ? "text-white font-medium"
                  : "text-muted-foreground"
              }`}
            >
              {step.label}
            </span>
            {i < steps.length - 1 && (
              <div
                className={`h-px w-8 ${
                  step.number < currentStep ? "bg-success" : "bg-white/10"
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step content */}
      <Card className="w-full max-w-lg">
        <CardContent className="p-8">
          {currentStep === 1 && (
            <StepCreateOrg onNext={handleOrgCreated} />
          )}
          {currentStep === 2 && orgId && (
            <StepConnectChannel
              orgId={orgId}
              onNext={handleChannelDone}
              onSkip={handleChannelDone}
            />
          )}
          {currentStep === 3 && orgId && (
            <StepConfigureEscalation
              orgId={orgId}
              onNext={handleEscalationDone}
              onSkip={handleEscalationDone}
            />
          )}
          {currentStep === 4 && orgId && (
            <StepComplete orgId={orgId} onFinish={handleFinish} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
